import { FC, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import { RefreshCw, Download, Calendar, AlertCircle } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import {
  ReconciliationSummaryCards,
  DiscrepancyTable,
  ReconciliationRunHistory,
  ResolveDiscrepancyModal,
} from '../../components/admin/reconciliation';
import { reconciliationApi } from '../../api/reconciliationApi';
import { useToast } from '../../components/ui/Toast';

export const ReconciliationDashboardPage: FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState<string | null>(null);

  // Fetch dashboard data
  const {
    data: dashboard,
    isLoading: isDashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useQuery({
    queryKey: ['reconciliation-dashboard', dateRange],
    queryFn: () => reconciliationApi.getDashboard(dateRange.startDate, dateRange.endDate),
  });

  // Fetch discrepancies
  const {
    data: discrepanciesData,
    isLoading: isDiscrepanciesLoading,
    error: discrepanciesError,
  } = useQuery({
    queryKey: ['reconciliation-discrepancies', dateRange],
    queryFn: () =>
      reconciliationApi.getDiscrepancies({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        pageSize: 10,
      }),
  });

  // Run reconciliation mutation
  const runMutation = useMutation({
    mutationFn: () =>
      reconciliationApi.runReconciliation(dateRange.startDate, dateRange.endDate, 'MANUAL'),
    onSuccess: (result) => {
      showToast({
        type: 'success',
        title: 'Reconciliation Complete',
        message: `${result.matchedCount} matched, ${result.discrepancyCount} discrepancies found`,
      });
      queryClient.invalidateQueries({ queryKey: ['reconciliation-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['reconciliation-discrepancies'] });
    },
    onError: (error: Error & { response?: { data?: { error?: string } } }) => {
      showToast({
        type: 'error',
        title: 'Reconciliation Failed',
        message: error.response?.data?.error || 'Failed to run reconciliation',
      });
    },
  });

  // Export handler
  const handleExport = useCallback(
    async (exportFormat: 'csv' | 'json') => {
      try {
        const blob = await reconciliationApi.exportPayments(
          dateRange.startDate,
          dateRange.endDate,
          exportFormat
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payments_${dateRange.startDate}_to_${dateRange.endDate}.${exportFormat}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showToast({
          type: 'success',
          title: 'Export Downloaded',
          message: `Payments exported as ${exportFormat.toUpperCase()}`,
        });
      } catch {
        showToast({
          type: 'error',
          title: 'Export Failed',
          message: 'Failed to export payments',
        });
      }
    },
    [dateRange, showToast]
  );

  const handleDiscrepancyResolved = useCallback(() => {
    setSelectedDiscrepancy(null);
    queryClient.invalidateQueries({ queryKey: ['reconciliation-discrepancies'] });
    queryClient.invalidateQueries({ queryKey: ['reconciliation-dashboard'] });
  }, [queryClient]);

  // Determine page state
  const isLoading = isDashboardLoading;
  const hasError = dashboardError || discrepanciesError;

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex h-96 items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
            <p className="text-gray-600 dark:text-slate-400">Loading reconciliation data...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (hasError) {
    return (
      <AdminLayout>
        <div className="flex h-96 items-center justify-center">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-4 h-12 w-12 text-red-500" />
            <h2 className="mb-2 text-xl font-bold text-gray-900 dark:text-slate-100">
              Error Loading Data
            </h2>
            <p className="mb-4 text-gray-600 dark:text-slate-400">
              {(dashboardError as Error)?.message || 'Failed to load reconciliation data'}
            </p>
            <button
              onClick={() => refetchDashboard()}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
              Payment Reconciliation
            </h2>
            <p className="mt-1 text-gray-500 dark:text-slate-400">
              Compare and reconcile payments between database and Stripe
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Date Range Inputs */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, startDate: e.target.value }))
                  }
                  className="rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                />
              </div>
              <span className="text-gray-500">to</span>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, endDate: e.target.value }))
                  }
                  className="rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
                />
              </div>
            </div>

            {/* Run Reconciliation Button */}
            <button
              onClick={() => runMutation.mutate()}
              disabled={runMutation.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${runMutation.isPending ? 'animate-spin' : ''}`}
              />
              {runMutation.isPending ? 'Running...' : 'Run Reconciliation'}
            </button>

            {/* Export Button */}
            <div className="relative group">
              <button
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
              <div className="absolute right-0 top-full z-10 mt-1 hidden w-32 rounded-lg border border-gray-200 bg-white shadow-lg group-hover:block dark:border-slate-600 dark:bg-slate-700">
                <button
                  onClick={() => handleExport('csv')}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-600"
                >
                  Export CSV
                </button>
                <button
                  onClick={() => handleExport('json')}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-600"
                >
                  Export JSON
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {dashboard && <ReconciliationSummaryCards summary={dashboard.summary} />}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Discrepancies Section */}
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                Recent Discrepancies
              </h3>
              {discrepanciesData && discrepanciesData.total > 10 && (
                <span className="text-sm text-gray-500 dark:text-slate-400">
                  Showing 10 of {discrepanciesData.total}
                </span>
              )}
            </div>
            {isDiscrepanciesLoading ? (
              <div className="flex h-48 items-center justify-center rounded-lg border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-800">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
              </div>
            ) : discrepanciesData ? (
              <DiscrepancyTable
                discrepancies={discrepanciesData.discrepancies}
                onResolve={setSelectedDiscrepancy}
              />
            ) : null}
          </div>

          {/* Run History Section */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-slate-100">
              Recent Runs
            </h3>
            {dashboard && <ReconciliationRunHistory runs={dashboard.recentRuns} />}
          </div>
        </div>

        {/* Discrepancies by Type */}
        {dashboard && Object.keys(dashboard.discrepanciesByType).length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-slate-100">
              Discrepancies by Type
            </h3>
            <div className="flex flex-wrap gap-4">
              {Object.entries(dashboard.discrepanciesByType).map(([type, count]) => (
                <div
                  key={type}
                  className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 dark:bg-slate-700"
                >
                  <span className="font-medium text-gray-900 dark:text-slate-100">
                    {type.replace(/_/g, ' ')}
                  </span>
                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-sm font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Resolve Modal */}
      {selectedDiscrepancy && (
        <ResolveDiscrepancyModal
          discrepancyId={selectedDiscrepancy}
          onClose={() => setSelectedDiscrepancy(null)}
          onResolved={handleDiscrepancyResolved}
        />
      )}
    </AdminLayout>
  );
};

export default ReconciliationDashboardPage;
